import React, { useState, useEffect } from 'react'
import { Input, InputGroup } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useLazyQuery } from '@apollo/client'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { t } from 'i18next'
import { BoatStatusDropdown } from 'components/custom/BoatStatus'
import { useNavigate } from 'react-router-dom'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { MagnifyingGlassIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { EyeIcon, ArrowLeftIcon, PencilIcon } from '@heroicons/react/20/solid'
import { SEARCH_BOAT_LIST } from 'graphql/boat/queries'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { BoatIcon } from 'components/icons'
import { getPageRange } from 'utils/paginationHelper'

export default function Boats() {
    const navigate = useNavigate()
    // Boat data
    const [searchString, setSearchString] = useState('')
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    const [boatList, setBoatList] = useState(null)
    const [boatCount, setBoatCount] = useState(0)
    const [searchBoatStatus, setSearchBoatStatus] = useState('ACT')

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(boatCount / rowsPerPage)

    const [getBoatList, { loading: boatListLoading, error: boatListError }] = useLazyQuery(SEARCH_BOAT_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchBoat.success && data.searchBoat.code === '200') {
                setBoatList(data.searchBoat.boats)
                setBoatCount(data.searchBoat.total_count)
            }
        },
    })

    // Search boats Boat List
    const searchBoats = (searchKey, status) => {
        getBoatList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                name: searchKey,
                searchBoatId: null,
                statusCode: status === 'All' ? null : status,
            },
        })
    }

    useEffect(() => {
        searchBoats(searchString, searchBoatStatus)
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const onClickSearch = () => {
        console.log('searchString', searchString)
        setPage(0)
        setRowsPerPage(10)
        searchBoats(searchString, searchBoatStatus) //
    }
    const onClickSearchClear = () => {
        console.log('searchString before clear:', searchString)
        setSearchString('') // Clear the search string
        setSearchBoatStatus('All')
        setPage(0)
        setRowsPerPage(10)
        searchBoats('', 'All') // Call searchBoats with an empty string after clearing
        // Let state update before invoking
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowConfirmDialog(false)
        setConfirmMessage('')
        setSuccessMessage('')
        setErrorMessage('')
    }
    const handleConfirmPositive = () => {}

    const handleConfirmNegative = () => {
        setShowConfirmDialog(false)
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3"> {t('Manage Your Boats')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t(' Manage boat details and monitor fleet status efficiently')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <button
                                    onClick={() => navigate('/boats/boat-form')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Boat')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-between align-items-center mt-4">
                        <div>
                            <div className="mt-4 mb-4 flex flex-col sm:flex-row  max-w-xl gap-4">
                                <div>
                                    <label
                                        htmlFor="country"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Status')}
                                    </label>
                                    <BoatStatusDropdown
                                        value={searchBoatStatus}
                                        onChange={e => {
                                            setSearchBoatStatus(e.target.value)
                                        }}
                                        error=""
                                        helperText=""
                                        purpose="filter"
                                    />
                                </div>
                                <div className="flex-full">
                                    <label
                                        htmlFor="country"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Search')}
                                    </label>
                                    <InputGroup>
                                        <MagnifyingGlassIcon />
                                        <Input
                                            name="search"
                                            id="search"
                                            value={searchString}
                                            onChange={e => setSearchString(e.target.value)}
                                            placeholder={t('Search boats')}
                                        />
                                    </InputGroup>
                                </div>
                                <div className="flex-none grow space-x-2 justify-end place-content-end">
                                    <button
                                        type="button"
                                        onClick={e => {
                                            e.stopPropagation() // Prevent the click event from bubbling up
                                            onClickSearch()
                                        }}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                    >
                                        {t('Search')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={e => {
                                            e.stopPropagation() // Prevent the click event from bubbling up
                                            onClickSearchClear()
                                        }}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Clear')}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div> </div>
                    </div>
                </div>
            </div>
            <Divider />
            {boatListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {boatListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch boats!')}</div>
                </div>
            )}
            {!boatListLoading && !boatListError && boatList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!boatListLoading && !boatListError && boatList?.length > 0 && (
                <>
                    <div className="table-custom flex justify-center items-center bg-white p-0">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    {/* <TableHeader>Boat ID</TableHeader> */}
                                    <TableHeader>{t('Boat Name')}</TableHeader>
                                    <TableHeader>{t('Boat Type')}</TableHeader>
                                    <TableHeader>{t('Reg No')}</TableHeader>
                                    <TableHeader>{t('Capacity')}</TableHeader>
                                    {/* <TableHeader>{t('Seat Layout')}</TableHeader> */}
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader className="flex justify-end">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {boatList.map(boat => (
                                    <TableRow
                                        key={boat.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/boats/boat-details/${boat.id}`)}
                                        title={`Boat #${boat.id}`}
                                    >
                                        {/* <TableCell>{boat.id}</TableCell> */}
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                <ImageDisplay
                                                    s3Key={boat.details.images[0]?.s3_key}
                                                    file=""
                                                    classStyle="w-[50px] h-[50px] sm:w-[30px] sm:h-[30px] rounded-full ring-4 ring-white max-w-none"
                                                />
                                                {/* <img
                                                src={boatImage}
                                                alt={`dd's avatar`}
                                                className="h-10 w-10 rounded-full object-cover"
                                            /> */}
                                                {boat.name.en}
                                            </div>
                                        </TableCell>
                                        <TableCell>{boat.details.boat_type.description.en}</TableCell>
                                        <TableCell>{boat.details.reg_number}</TableCell>
                                        <TableCell>{boat.details.capacity}</TableCell>
                                        {/* <TableCell>{boat.seat_layout.layout_key}</TableCell> */}
                                        <TableCell>
                                            <Badge
                                                color={
                                                    boat.status.code === 'ACT'
                                                        ? 'emerald'
                                                        : boat.status.code === 'UMT'
                                                        ? 'amber'
                                                        : boat.status.code === 'INA'
                                                        ? 'red'
                                                        : 'rose'
                                                }
                                            >
                                                {boat.status.description.en}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/boats/boat-details/${boat.id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/boats/boat-form/${boat.id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title="Boat Status Update"
                    negativeActionText="Cancel"
                    positiveActionText="Yes"
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
        </>
    )
}
